/**
 * WORLD RENDERER (SVG)
 *
 * Isometric world view using SVG for crisp, anti-aliased rendering.
 * Visual style adapted from ColonyNewDesign WorldView.
 * Supports pan/zoom, edit-mode drag, zone resize, pipelines, agents.
 */

import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { useWorldStore, type Pipeline, type ProjectShape } from "@/stores/worldStore";
import { useSelectionStore } from "@/stores/selectionStore";
import { useUIStore } from "@/stores/uiStore";
import { useCloudStore } from "@/stores/cloudStore";
import { useUnifiedAgents } from "@/hooks/useUnifiedAgents";
import { unifiedStatusToAgentVisuals } from "@/lib/utils/agent-status";
import type { Project, Agent, Zone } from "@/stores/worldStore";

// ─── Constants ──────────────────────────────────────────
const TILE_W = 64;
const TILE_H = 32;
const GRID_SIZE = 12;

// Status colors — matches ColonyNewDesign
type StatusColorSet = { main: string; dark: string; top: string; light: string; glow: string };

const FAILING_COLORS: StatusColorSet = { main: "#ef4444", dark: "#dc2626", top: "#fca5a5", light: "#f87171", glow: "rgba(239,68,68,0.25)" };

const STATUS_COLORS: Record<string, StatusColorSet> = {
  healthy: { main: "#22c55e", dark: "#16a34a", top: "#4ade80", light: "#2dd06b", glow: "rgba(34,197,94,0.25)" },
  degraded: { main: "#f59e0b", dark: "#d97706", top: "#fcd34d", light: "#fbbf24", glow: "rgba(245,158,11,0.25)" },
  failing: FAILING_COLORS,
};

// Marketing status colors — purple/magenta palette (shares failing with coding)
const MARKETING_STATUS_COLORS: Record<string, StatusColorSet> = {
  healthy: { main: "#a855f7", dark: "#7c3aed", top: "#c084fc", light: "#b06afc", glow: "rgba(168,85,247,0.25)" },
  degraded: { main: "#e879f9", dark: "#c026d3", top: "#f0abfc", light: "#e18ff5", glow: "rgba(232,121,249,0.25)" },
  failing: FAILING_COLORS,
};

function getStatusColors(project: Project) {
  const palette = project.project_category === "marketing" ? MARKETING_STATUS_COLORS : STATUS_COLORS;
  return palette[project.status] || palette.healthy;
}

const AGENT_COLORS: Record<string, string> = {
  idle: "#6b7280",
  busy: "#3b82f6",
  queued: "#a78bfa",
};

const MARKETING_AGENT_COLORS: Record<string, string> = {
  idle: "#6b7280",
  busy: "#a855f7",
  queued: "#c084fc",
};

const ATTENTION_COLORS: Record<string, { bg: string }> = {
  none: { bg: "transparent" },
  completed: { bg: "#22c55e" },
  stuck: { bg: "#ef4444" },
  human_needed: { bg: "#f59e0b" },
  error: { bg: "#dc2626" },
};

const CLOUD_RESOURCE_COLORS: Record<string, string> = {
  lambda: "#F79400", s3: "#3F8624", rds: "#3B48CC", ecs: "#ED7100",
  apigateway: "#A166FF", dynamodb: "#4053D6", sqs: "#FF9900",
  cloudfront: "#8C4FFF", ec2: "#FF9900", elasticache: "#C925D1", sns: "#D6336C",
};

// Shape dimensions — matches ColonyNewDesign proportions
// ColonyNewDesign uses per-project w/d/h (e.g. hexagon 2×2×3, box 1×1×4, cylinder 2×2×2.5)
const SHAPE_DIMS: Record<ProjectShape, { w: number; d: number; h: number }> = {
  square: { w: 1.5, d: 1.5, h: 3 },
  rectangle: { w: 2, d: 1.2, h: 2.5 },
  hexagon: { w: 2, d: 2, h: 3 },
  circle: { w: 2, d: 2, h: 2.5 },
  database: { w: 1.8, d: 1.8, h: 3 },
  gear: { w: 2, d: 2, h: 2.5 },
  server: { w: 0.7, d: 0.7, h: 5 },
  factory: { w: 2, d: 1.5, h: 3 },
};

// ─── Helpers ────────────────────────────────────────────
function gridToScreen(gx: number, gy: number): [number, number] {
  return [(gx - gy) * (TILE_W / 2), (gx + gy) * (TILE_H / 2)];
}

// ─── SVG Building Shapes ────────────────────────────────
interface ShapeProps {
  bx: number;
  by: number;
  w: number;
  d: number;
  h: number;
  sc: { main: string; dark: string; top: string; light: string; glow: string };
  isSelected: boolean;
}

function IsometricBox({ bx, by, w, d, h, sc, isSelected }: ShapeProps) {
  const bh = h * 16;
  const tw = (w * TILE_W) / 2;
  const td = (d * TILE_H) / 2;

  const bl: [number, number] = [bx - tw, by];
  const bt: [number, number] = [bx, by - td];
  const br: [number, number] = [bx + tw, by];
  const bb: [number, number] = [bx, by + td];
  const tl: [number, number] = [bl[0], bl[1] - bh];
  const tt: [number, number] = [bt[0], bt[1] - bh];
  const tr: [number, number] = [br[0], br[1] - bh];
  const tb: [number, number] = [bb[0], bb[1] - bh];

  const topFace = `${tl[0]},${tl[1]} ${tt[0]},${tt[1]} ${tr[0]},${tr[1]} ${tb[0]},${tb[1]}`;
  const leftFace = `${tl[0]},${tl[1]} ${tb[0]},${tb[1]} ${bb[0]},${bb[1]} ${bl[0]},${bl[1]}`;
  const rightFace = `${tr[0]},${tr[1]} ${tb[0]},${tb[1]} ${bb[0]},${bb[1]} ${br[0]},${br[1]}`;

  return (
    <>
      <polygon points={leftFace} fill={sc.dark} stroke={isSelected ? "#fff" : sc.main} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.9} />
      <polygon points={rightFace} fill={sc.main} stroke={isSelected ? "#fff" : sc.main} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.8} />
      <polygon points={topFace} fill={sc.top} stroke={isSelected ? "#fff" : sc.light} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.7} />
      {isSelected && <polygon points={topFace} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth={2} strokeDasharray="4,3" />}
    </>
  );
}

function IsometricHexagon({ bx, by, w, d, h, sc, isSelected }: ShapeProps) {
  const bh = h * 16;
  const r = Math.min(w, d) * TILE_W * 0.35;
  const topY = by - bh;

  const hexPts = (cy: number): string => {
    const pts: string[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 6;
      pts.push(`${bx + r * Math.cos(a)},${cy + r * Math.sin(a) * 0.5}`);
    }
    return pts.join(" ");
  };

  const topPts: [number, number][] = [];
  const botPts: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    topPts.push([bx + r * Math.cos(a), topY + r * Math.sin(a) * 0.5]);
    botPts.push([bx + r * Math.cos(a), by + r * Math.sin(a) * 0.5]);
  }

  const sideFaces: string[] = [];
  for (let i = 1; i <= 4; i++) {
    const ni = (i + 1) % 6;
    sideFaces.push(
      `${topPts[i][0]},${topPts[i][1]} ${topPts[ni][0]},${topPts[ni][1]} ${botPts[ni][0]},${botPts[ni][1]} ${botPts[i][0]},${botPts[i][1]}`
    );
  }

  return (
    <>
      {sideFaces.map((face, i) => (
        <polygon key={i} points={face} fill={i < 2 ? sc.dark : sc.main} stroke={isSelected ? "#fff" : sc.main} strokeWidth={isSelected ? 1.5 : 0.3} opacity={0.85 - i * 0.05} />
      ))}
      <polygon points={hexPts(topY)} fill={sc.top} stroke={isSelected ? "#fff" : sc.light} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.7} />
      {isSelected && <polygon points={hexPts(topY)} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth={2} strokeDasharray="4,3" />}
    </>
  );
}

function IsometricCylinder({ bx, by, w, d, h, sc, isSelected }: ShapeProps) {
  const bh = h * 16;
  const rx = w * TILE_W * 0.3;
  const ry = d * TILE_H * 0.3;
  const topCy = by - bh;

  const sidePath = [
    `M ${bx - rx} ${topCy}`,
    `L ${bx - rx} ${by}`,
    `A ${rx} ${ry} 0 0 0 ${bx + rx} ${by}`,
    `L ${bx + rx} ${topCy}`,
    `A ${rx} ${ry} 0 0 1 ${bx - rx} ${topCy}`,
  ].join(" ");

  return (
    <>
      <path d={sidePath} fill={sc.main} stroke={isSelected ? "#fff" : sc.main} strokeWidth={isSelected ? 1.5 : 0.3} opacity={0.85} />
      <ellipse cx={bx} cy={by} rx={rx} ry={ry} fill={sc.dark} stroke={sc.main} strokeWidth={0.3} opacity={0.5} />
      <ellipse cx={bx} cy={topCy} rx={rx} ry={ry} fill={sc.top} stroke={isSelected ? "#fff" : sc.light} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.7} />
      {isSelected && <ellipse cx={bx} cy={topCy} rx={rx} ry={ry} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth={2} strokeDasharray="4,3" />}
    </>
  );
}

function IsometricGear({ bx, by, w, d, h, sc, isSelected }: ShapeProps) {
  const bh = h * 16;
  const outerR = Math.min(w, d) * TILE_W * 0.38;
  const innerR = outerR * 0.7;
  const teeth = 8;
  const topY = by - bh;

  const gearPts = (cy: number): string => {
    const pts: string[] = [];
    for (let i = 0; i < teeth * 2; i++) {
      const a = (Math.PI / teeth) * i - Math.PI / 2;
      const r = i % 2 === 0 ? outerR : innerR;
      pts.push(`${bx + r * Math.cos(a)},${cy + r * Math.sin(a) * 0.5}`);
    }
    return pts.join(" ");
  };

  const topPts: [number, number][] = [];
  const botPts: [number, number][] = [];
  for (let i = 0; i < teeth * 2; i++) {
    const a = (Math.PI / teeth) * i - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    topPts.push([bx + r * Math.cos(a), topY + r * Math.sin(a) * 0.5]);
    botPts.push([bx + r * Math.cos(a), by + r * Math.sin(a) * 0.5]);
  }

  const visibleSides: { pts: string; isLeft: boolean }[] = [];
  for (let i = 0; i < topPts.length; i++) {
    const ni = (i + 1) % topPts.length;
    if (topPts[i][1] > topY - 5 || topPts[ni][1] > topY - 5) {
      visibleSides.push({
        pts: `${topPts[i][0]},${topPts[i][1]} ${topPts[ni][0]},${topPts[ni][1]} ${botPts[ni][0]},${botPts[ni][1]} ${botPts[i][0]},${botPts[i][1]}`,
        isLeft: topPts[i][0] < bx,
      });
    }
  }

  return (
    <>
      {visibleSides.map((side, i) => (
        <polygon key={i} points={side.pts} fill={side.isLeft ? sc.dark : sc.main} stroke={isSelected ? "#fff" : sc.main} strokeWidth={isSelected ? 1.5 : 0.3} opacity={side.isLeft ? 0.9 : 0.8} />
      ))}
      <polygon points={gearPts(topY)} fill={sc.top} stroke={isSelected ? "#fff" : sc.light} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.7} />
      <ellipse cx={bx} cy={topY} rx={5} ry={2.5} fill="#0F0F11" opacity={0.9} />
      {isSelected && <polygon points={gearPts(topY)} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth={2} strokeDasharray="4,3" />}
    </>
  );
}

function IsometricDatabase({ bx, by, w, d, h, sc, isSelected }: ShapeProps) {
  const bh = h * 16;
  const rx = w * TILE_W * 0.3;
  const ry = d * TILE_H * 0.3;
  const topCy = by - bh;

  const sidePath = [
    `M ${bx - rx} ${topCy}`,
    `L ${bx - rx} ${by}`,
    `A ${rx} ${ry} 0 0 0 ${bx + rx} ${by}`,
    `L ${bx + rx} ${topCy}`,
    `A ${rx} ${ry} 0 0 1 ${bx - rx} ${topCy}`,
  ].join(" ");

  const tierLines = [];
  for (let i = 1; i < 3; i++) {
    const lineY = topCy + (bh / 3) * i;
    tierLines.push(
      <ellipse key={i} cx={bx} cy={lineY} rx={rx} ry={ry * 0.7} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth={0.8} opacity={0.6} />
    );
  }

  return (
    <>
      <path d={sidePath} fill={sc.main} stroke={isSelected ? "#fff" : sc.main} strokeWidth={isSelected ? 1.5 : 0.3} opacity={0.85} />
      <ellipse cx={bx} cy={by} rx={rx} ry={ry} fill={sc.dark} stroke={sc.main} strokeWidth={0.3} opacity={0.5} />
      {tierLines}
      <ellipse cx={bx} cy={topCy} rx={rx} ry={ry} fill={sc.top} stroke={isSelected ? "#fff" : sc.light} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.7} />
      {isSelected && <ellipse cx={bx} cy={topCy} rx={rx} ry={ry} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth={2} strokeDasharray="4,3" />}
    </>
  );
}

function IsometricFactory({ bx, by, w, d, h, sc, isSelected }: ShapeProps) {
  const mainH = h * 16 * 0.6;
  const tw = (w * TILE_W) / 2;
  const td = (d * TILE_H) / 2;

  const bl: [number, number] = [bx - tw, by];
  const bt: [number, number] = [bx, by - td];
  const br: [number, number] = [bx + tw, by];
  const bb: [number, number] = [bx, by + td];
  const tl: [number, number] = [bl[0], bl[1] - mainH];
  const tt: [number, number] = [bt[0], bt[1] - mainH];
  const tr: [number, number] = [br[0], br[1] - mainH];
  const tb: [number, number] = [bb[0], bb[1] - mainH];

  const topFace = `${tl[0]},${tl[1]} ${tt[0]},${tt[1]} ${tr[0]},${tr[1]} ${tb[0]},${tb[1]}`;
  const leftFace = `${tl[0]},${tl[1]} ${tb[0]},${tb[1]} ${bb[0]},${bb[1]} ${bl[0]},${bl[1]}`;
  const rightFace = `${tr[0]},${tr[1]} ${tb[0]},${tb[1]} ${bb[0]},${bb[1]} ${br[0]},${br[1]}`;

  const chW = 4;
  const chH = 14;
  const chD = 3;
  const chX = bx - tw * 0.4;
  const chBaseY = tl[1] - td * 0.3;

  const chTop = `${chX},${chBaseY - chH - chD} ${chX + chW},${chBaseY - chH} ${chX},${chBaseY - chH + chD} ${chX - chW},${chBaseY - chH}`;
  const chLeft = `${chX - chW},${chBaseY - chH} ${chX},${chBaseY - chH + chD} ${chX},${chBaseY + chD} ${chX - chW},${chBaseY}`;
  const chRight = `${chX + chW},${chBaseY - chH} ${chX},${chBaseY - chH + chD} ${chX},${chBaseY + chD} ${chX + chW},${chBaseY}`;

  return (
    <>
      <polygon points={leftFace} fill={sc.dark} stroke={isSelected ? "#fff" : sc.main} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.9} />
      <polygon points={rightFace} fill={sc.main} stroke={isSelected ? "#fff" : sc.main} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.8} />
      <polygon points={topFace} fill={sc.top} stroke={isSelected ? "#fff" : sc.light} strokeWidth={isSelected ? 1.5 : 0.5} opacity={0.7} />
      <polygon points={chLeft} fill="#4b5563" opacity={0.9} />
      <polygon points={chRight} fill="#374151" opacity={0.8} />
      <polygon points={chTop} fill="#6b7280" opacity={0.7} />
      {isSelected && <polygon points={topFace} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth={2} strokeDasharray="4,3" />}
    </>
  );
}

// Shape renderer
function renderShape(shape: ProjectShape, props: ShapeProps) {
  switch (shape) {
    case "hexagon": return <IsometricHexagon {...props} />;
    case "gear": return <IsometricGear {...props} />;
    case "circle": return <IsometricCylinder {...props} />;
    case "database": return <IsometricDatabase {...props} />;
    case "factory": return <IsometricFactory {...props} />;
    case "server": return <IsometricBox {...props} />;
    case "rectangle": return <IsometricBox {...props} />;
    default: return <IsometricBox {...props} />;
  }
}

// ─── SVG Grid ───────────────────────────────────────────
function IsoGrid({ visible, isDark }: { visible: boolean; isDark: boolean }) {
  if (!visible) return null;
  const lines: React.ReactNode[] = [];

  for (let i = 0; i <= GRID_SIZE; i++) {
    const [x1, y1] = gridToScreen(i, 0);
    const [x2, y2] = gridToScreen(i, GRID_SIZE);
    lines.push(<line key={`a${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={isDark ? "#2A2A2F" : "rgba(0,0,0,0.06)"} strokeWidth={0.5} opacity={0.5} />);

    const [x3, y3] = gridToScreen(0, i);
    const [x4, y4] = gridToScreen(GRID_SIZE, i);
    lines.push(<line key={`b${i}`} x1={x3} y1={y3} x2={x4} y2={y4} stroke={isDark ? "#2A2A2F" : "rgba(0,0,0,0.06)"} strokeWidth={0.5} opacity={0.5} />);
  }

  return <g>{lines}</g>;
}

// ─── SVG Particles ──────────────────────────────────────
function Particles({ count = 20 }: { count?: number }) {
  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        x: ((Math.sin(i * 127.1 + 311.7) * 43758.5453 % 1) - 0.5) * GRID_SIZE * TILE_W * 0.8,
        y: ((Math.sin(i * 269.5 + 183.3) * 43758.5453 % 1) - 0.5) * GRID_SIZE * TILE_H * 2,
        r: 0.8 + (i % 3) * 0.5,
        dur: 4 + (i % 5) * 2,
        delay: (i * 0.7) % 5,
        op: 0.15 + (i % 4) * 0.05,
      })),
    [count]
  );

  return (
    <g>
      {particles.map((p) => (
        <circle key={p.id} cx={p.x} cy={p.y} r={p.r} fill="#00B2C8" opacity={0}>
          <animate attributeName="opacity" values={`0;${p.op};0`} dur={`${p.dur}s`} begin={`${p.delay}s`} repeatCount="indefinite" />
          <animate attributeName="cy" values={`${p.y};${p.y - 40};${p.y}`} dur={`${p.dur}s`} begin={`${p.delay}s`} repeatCount="indefinite" />
        </circle>
      ))}
    </g>
  );
}

// ─── SVG Zone ───────────────────────────────────────────
function ZoneOverlay({ zone, isSelected, editMode, showLabel }: { zone: Zone; isSelected: boolean; editMode: boolean; showLabel: boolean }) {
  const corners = [
    gridToScreen(zone.startX, zone.startY),
    gridToScreen(zone.startX + zone.width, zone.startY),
    gridToScreen(zone.startX + zone.width, zone.startY + zone.height),
    gridToScreen(zone.startX, zone.startY + zone.height),
  ];
  const pts = corners.map((c) => `${c[0]},${c[1]}`).join(" ");
  const [brX, brY] = corners[2];

  return (
    <g>
      <polygon
        points={pts}
        fill={zone.color}
        fillOpacity={0.06}
        stroke={isSelected && editMode ? "#3b82f6" : zone.color}
        strokeWidth={isSelected && editMode ? 2 : 1}
        strokeDasharray={isSelected && editMode ? "5,5" : "6,4"}
        strokeOpacity={isSelected && editMode ? 1 : 0.3}
      />
      {isSelected && editMode && (
        <circle cx={brX} cy={brY} r={6} fill="#3b82f6" stroke="#ffffff" strokeWidth={2} />
      )}
      {showLabel && (
        <g transform={`translate(${corners[0][0] + 8}, ${corners[0][1] + 4})`}>
          <rect x={-2} y={-9} width={zone.name.length * 5.5 + 12} height={14} rx={3} fill={zone.color} fillOpacity={0.15} stroke={zone.color} strokeWidth={0.5} strokeOpacity={0.3} />
          <text x={4} y={1.5} fill={zone.color} fontSize={9} fontFamily="Inter, sans-serif" fontWeight={500} opacity={0.7}>{zone.name}</text>
        </g>
      )}
    </g>
  );
}

// ─── SVG Pipeline ───────────────────────────────────────
function PipelineSVG({ pipeline, projects, zones, isDark }: { pipeline: Pipeline; projects: Project[]; zones: Zone[]; isDark: boolean }) {
  let fromPos: [number, number] | null = null;
  let toPos: [number, number] | null = null;

  if (pipeline.fromType === "project") {
    const p = projects.find((pr) => pr.id === pipeline.fromId);
    if (p) { const [x, y] = gridToScreen(p.x, p.y); fromPos = [x, y - 20]; }
  } else {
    const z = zones.find((zn) => zn.id === pipeline.fromId);
    if (z) {
      const [x1, y1] = gridToScreen(z.startX, z.startY);
      const [x2, y2] = gridToScreen(z.startX + z.width, z.startY + z.height);
      fromPos = [(x1 + x2) / 2, (y1 + y2) / 2];
    }
  }

  if (pipeline.toType === "project") {
    const p = projects.find((pr) => pr.id === pipeline.toId);
    if (p) { const [x, y] = gridToScreen(p.x, p.y); toPos = [x, y - 20]; }
  } else {
    const z = zones.find((zn) => zn.id === pipeline.toId);
    if (z) {
      const [x1, y1] = gridToScreen(z.startX, z.startY);
      const [x2, y2] = gridToScreen(z.startX + z.width, z.startY + z.height);
      toPos = [(x1 + x2) / 2, (y1 + y2) / 2];
    }
  }

  if (!fromPos || !toPos) return null;

  const style = pipeline.style || "solid";
  const traffic = pipeline.traffic || "normal";
  const baseColor = pipeline.color || (isDark ? "rgba(96,165,250,0.5)" : "rgba(59,130,246,0.4)");
  let lineWidth = 1.5;
  let opacity = 0.5;
  if (traffic === "heavy") { lineWidth = 2.5; opacity = 0.7; }
  else if (traffic === "light") { lineWidth = 1; opacity = 0.3; }
  else if (traffic === "none") { lineWidth = 1; opacity = 0.15; }

  const dashArray = style === "dashed" ? "8,4" : style === "dotted" ? "2,3" : undefined;
  const midX = (fromPos[0] + toPos[0]) / 2;
  const midY = (fromPos[1] + toPos[1]) / 2 - 15;
  const pathD = `M ${fromPos[0]} ${fromPos[1]} Q ${midX} ${midY} ${toPos[0]} ${toPos[1]}`;

  const dx = toPos[0] - fromPos[0];
  const dy = toPos[1] - fromPos[1];
  const angle = Math.atan2(dy, dx);
  const arrowSize = 5;

  return (
    <g>
      <path d={pathD} fill="none" stroke={baseColor} strokeWidth={lineWidth} opacity={opacity} strokeDasharray={dashArray} />
      {pipeline.animated && traffic !== "none" && (
        <path d={pathD} fill="none" stroke={isDark ? "rgba(255,255,255,0.3)" : "rgba(59,130,246,0.4)"} strokeWidth={1} opacity={0.4} strokeDasharray="3,6">
          <animate attributeName="stroke-dashoffset" from="0" to="-9" dur="1s" repeatCount="indefinite" />
        </path>
      )}
      {(pipeline.direction === "forward" || pipeline.direction === "bidirectional") && (
        <polygon
          points={`0,0 ${-arrowSize},${-arrowSize / 2} ${-arrowSize},${arrowSize / 2}`}
          fill={baseColor}
          opacity={opacity + 0.2}
          transform={`translate(${toPos[0] - Math.cos(angle) * 2},${toPos[1] - Math.sin(angle) * 2}) rotate(${(angle * 180) / Math.PI})`}
        />
      )}
      {(pipeline.direction === "backward" || pipeline.direction === "bidirectional") && (
        <polygon
          points={`0,0 ${-arrowSize},${-arrowSize / 2} ${-arrowSize},${arrowSize / 2}`}
          fill={baseColor}
          opacity={opacity + 0.2}
          transform={`translate(${fromPos[0] + Math.cos(angle) * 2},${fromPos[1] + Math.sin(angle) * 2}) rotate(${((angle + Math.PI) * 180) / Math.PI})`}
        />
      )}
    </g>
  );
}

// ─── Agent Robot Avatar ───────
function AgentRobotSVG({
  x,
  y,
  color,
  speed,
  attentionState,
  attentionColor,
}: {
  x: number;
  y: number;
  color: string;
  speed: number;
  attentionState: string;
  attentionColor: string;
}) {
  const s = 0.9;
  return (
    <g transform={`translate(${x}, ${y}) scale(${s})`}>
      {/* Antenna */}
      <line x1={0} y1={-11} x2={0} y2={-8} stroke={color} strokeWidth={1.2} strokeLinecap="round" />
      <circle cx={0} cy={-12} r={1.5} fill={color} />

      {/* Head */}
      <rect x={-5} y={-8} width={10} height={8} rx={2} fill={color} opacity={0.9} />

      {/* Eyes */}
      <circle cx={-2.2} cy={-4.5} r={1.2} fill="#ffffff" />
      <circle cx={2.2} cy={-4.5} r={1.2} fill="#ffffff" />

      {/* Body */}
      <rect x={-4} y={1} width={8} height={6} rx={1.5} fill={color} opacity={0.75} />

      {/* Opacity pulse when busy */}
      {speed > 0 && (
        <animate
          attributeName="opacity"
          values="1;0.6;1"
          dur={`${1.5 / Math.max(speed, 0.1)}s`}
          repeatCount="indefinite"
        />
      )}

      {/* Attention badge */}
      {attentionState !== "none" && (
        <g>
          <circle cx={6} cy={-10} r={3.5} fill={attentionColor} stroke="#ffffff" strokeWidth={0.8} />
          <text x={6} y={-8.2} textAnchor="middle" fill="#ffffff" fontSize={5} fontFamily="Inter, sans-serif" fontWeight={700}>
            {attentionState === "completed" ? "✓" : attentionState === "stuck" ? "!" : attentionState === "human_needed" ? "?" : "×"}
          </text>
        </g>
      )}
    </g>
  );
}

// ─── SVG Building (shape + glow + label + agents) ───────
function BuildingSVG({
  project,
  agents,
  isSelected,
  editMode,
  pulse,
}: {
  project: Project;
  agents: Agent[];
  isSelected: boolean;
  editMode: boolean;
  pulse: number;
}) {
  const [bx, by] = gridToScreen(project.x, project.y);
  const sc = getStatusColors(project);
  const shape = project.shape || (project.project_category === "marketing" ? "hexagon" : "square");
  const dims = SHAPE_DIMS[shape];
  const bh = dims.h * 16;
  const tw = (dims.w * TILE_W) / 2;
  const td = (dims.d * TILE_H) / 2;

  const projectAgents = agents;
  const projectAgentColors = project.project_category === "marketing" ? MARKETING_AGENT_COLORS : AGENT_COLORS;
  const busyCount = projectAgents.filter((a) => a.state === "busy").length;

  // Glow opacity — subtle pulse matching ColonyNewDesign: 0.15 + sin(pulse) * 0.1
  const glowOp = 0.15 + Math.sin(pulse) * 0.1;

  return (
    <g>
      {/* Glow ellipse */}
      <ellipse cx={bx} cy={by + 4} rx={tw + 8} ry={td + 4} fill={sc.glow} opacity={glowOp} />

      {/* Building shape */}
      {renderShape(shape, { bx, by, w: dims.w, d: dims.d, h: dims.h, sc, isSelected })}

      {/* Edit mode dashed ring */}
      {isSelected && editMode && (
        <circle cx={bx} cy={by - bh / 2} r={35} fill="none" stroke="#3b82f6" strokeWidth={2} strokeDasharray="4,4" />
      )}

      {/* Agent robot avatars — orbit around building */}
      {projectAgents.slice(0, 6).map((agent, index) => {
        const agentColor = projectAgentColors[agent.state] || projectAgentColors.idle;
        const baseAngle = (index / Math.min(projectAgents.length, 6)) * 360;
        const speed = agent.state === "busy" ? 0.8 : agent.state === "queued" ? 0.3 : 0;
        const distance = 28 + (index % 3) * 5;
        const orbAngle = baseAngle + (speed > 0 ? pulse * speed * 30 : 0);
        const rad = (orbAngle * Math.PI) / 180;
        const ax = bx + Math.cos(rad) * distance;
        const ay = by - bh * 0.5 + Math.sin(rad) * distance * 0.5;
        const attentionState = agent.attentionState || "none";
        const attentionColor = ATTENTION_COLORS[attentionState]?.bg || "transparent";

        return (
          <AgentRobotSVG
            key={agent.id}
            x={ax}
            y={ay}
            color={agentColor}
            speed={speed}
            attentionState={attentionState}
            attentionColor={attentionColor}
          />
        );
      })}

      {/* Agent count badge */}
      {projectAgents.length > 0 && (
        <g transform={`translate(${bx}, ${by - bh - 14})`}>
          <rect x={-18} y={-7} width={36} height={14} rx={3} fill="rgba(0,0,0,0.7)" />
          <text textAnchor="middle" y={4} fill="#ffffff" fontSize={9} fontFamily="Inter, sans-serif" fontWeight={700}>
            {busyCount}/{projectAgents.length}
          </text>
        </g>
      )}

      {/* Project name label */}
      <g transform={`translate(${bx}, ${by + td + 12})`}>
        <rect
          x={-project.name.length * 3.2 - 8}
          y={-7}
          width={project.name.length * 6.4 + 16}
          height={16}
          rx={4}
          fill="rgba(15,15,17,0.85)"
          stroke="rgba(42,42,47,0.5)"
          strokeWidth={0.5}
        />
        <text textAnchor="middle" y={4.5} fill="#D5D5DB" fontSize={10} fontFamily="Inter, sans-serif" fontWeight={500}>
          {project.name}
        </text>
      </g>
    </g>
  );
}

// ─── SVG Cloud Resources ────────────────────────────────
function CloudResourceDots({ project, resources }: { project: Project; resources: { resourceType: string }[] }) {
  const [bx, by] = gridToScreen(project.x, project.y);
  const maxVisible = Math.min(resources.length, 8);
  const radius = 32;

  return (
    <g>
      {resources.slice(0, maxVisible).map((resource, index) => {
        const angle = (index / maxVisible) * Math.PI * 2 + Math.PI / 6;
        const cx = bx + Math.cos(angle) * radius;
        const cy = by - 20 + Math.sin(angle) * radius * 0.5;
        const color = CLOUD_RESOURCE_COLORS[resource.resourceType] || "#6b7280";
        return (
          <circle key={index} cx={cx} cy={cy} r={3} fill={color} stroke="#ffffff" strokeWidth={0.8}>
            <animate attributeName="cy" values={`${cy};${cy - 1.5};${cy}`} dur="2s" begin={`${index * 0.3}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      {resources.length > 8 && (
        <text x={bx + 24} y={by - 42} textAnchor="middle" fill="#8b5cf6" fontSize={7} fontFamily="Inter, sans-serif" fontWeight={700}>
          ☁{resources.length}
        </text>
      )}
    </g>
  );
}

// ─── Hit Testing (for click detection) ──────────────────
function findProjectAtPosition(localX: number, localY: number, projects: Project[]): Project | null {
  for (const project of projects) {
    const [sx, sy] = gridToScreen(project.x, project.y);
    const dist = Math.sqrt((localX - sx) ** 2 + (localY - sy + 20) ** 2);
    if (dist < 35) return project;
  }
  return null;
}

function findZoneAtPosition(localX: number, localY: number, zones: Zone[]): Zone | null {
  for (const zone of zones) {
    const [x1, y1] = gridToScreen(zone.startX, zone.startY);
    const [x2, y2] = gridToScreen(zone.startX + zone.width, zone.startY + zone.height);
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    const maxDist = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1)) / 2;
    const dist = Math.sqrt((localX - cx) ** 2 + (localY - cy) ** 2);
    if (dist < maxDist) return zone;
  }
  return null;
}

// Static agent positions for click detection (ignores orbit)
function getAgentPositions(projects: Project[], agents: Agent[]) {
  const agentsByProject = new Map<number, Agent[]>();
  for (const agent of agents) {
    const list = agentsByProject.get(agent.projectId);
    if (list) list.push(agent);
    else agentsByProject.set(agent.projectId, [agent]);
  }
  const positions: { agent: Agent; x: number; y: number; index: number }[] = [];
  for (const project of projects) {
    const projectAgents = agentsByProject.get(project.id);
    if (!projectAgents || projectAgents.length === 0) continue;
    const [sx, sy] = gridToScreen(project.x, project.y);
    const shape = project.shape || "square";
    const bh = SHAPE_DIMS[shape].h * 16;
    const maxAgents = Math.min(projectAgents.length, 6);
    for (let i = 0; i < maxAgents; i++) {
      const baseAngle = (i / maxAgents) * 360;
      const rad = (baseAngle * Math.PI) / 180;
      const distance = 24 + (i % 3) * 4;
      positions.push({ agent: projectAgents[i], x: sx + Math.cos(rad) * distance, y: sy - bh * 0.5 + Math.sin(rad) * distance * 0.5, index: i });
    }
  }
  return positions;
}

function findAgentAtPosition(localX: number, localY: number, projects: Project[], agents: Agent[]): Agent | null {
  const positions = getAgentPositions(projects, agents);
  for (const { agent, x, y } of positions) {
    if (Math.sqrt((localX - x) ** 2 + (localY - y) ** 2) < 12) return agent;
  }
  return null;
}

// ─── District Labels & Divider ───────────────────────────
function DistrictLabel({ gx, gy, label, color }: { gx: number; gy: number; label: string; color: string }) {
  const [sx, sy] = gridToScreen(gx, gy);
  return (
    <g transform={`translate(${sx}, ${sy})`} opacity={0.5}>
      <text
        textAnchor="middle"
        y={0}
        fill={color}
        fontSize={11}
        fontFamily="Inter, sans-serif"
        fontWeight={600}
        letterSpacing="0.08em"
        style={{ textTransform: "uppercase" } as React.CSSProperties}
      >
        {label}
      </text>
      <line x1={-40} y1={6} x2={40} y2={6} stroke={color} strokeWidth={0.5} opacity={0.4} />
    </g>
  );
}

function DistrictDivider({ isDark }: { isDark: boolean }) {
  const [x1, y1] = gridToScreen(0, 8);
  const [x2, y2] = gridToScreen(GRID_SIZE, 8);
  const color = isDark ? "rgba(168,85,247,0.15)" : "rgba(168,85,247,0.1)";
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={1} strokeDasharray="6,4" />
    </g>
  );
}

// ─── Main Component ─────────────────────────────────────
export function PixiWorld() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const { projects, agents, zones, pipelines, updateProject, updateZone } = useWorldStore();
  const unifiedGroups = useUnifiedAgents();
  const cloudResources = useCloudStore((s) => s.resources);
  const { selectProject, selectAgent, selectZone, selectedType, selectedId } = useSelectionStore();
  const selectedProjectId = selectedType === "project" ? (selectedId as number) : null;
  const selectedAgentId = selectedType === "agent" ? (selectedId as string) : null;
  const selectedZoneId = selectedType === "zone" ? (selectedId as number) : null;
  const { zoom, cameraX, cameraY, setZoom, setCamera, setPanning, theme, editMode, showGrid, showZoneLabels } = useUIStore();

  const isDark = theme === "dark";

  // Animation pulse — drives glow + agent orbiting (matches ColonyNewDesign)
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    let frame: number;
    const start = performance.now();
    const animate = (time: number) => {
      setPulse((time - start) / 1000);
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  // Refs for pointer event state
  const isDragging = useRef(false);
  const isDraggingProject = useRef(false);
  const isDraggingZone = useRef(false);
  const isResizingZone = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const cameraStart = useRef({ x: 0, y: 0 });
  const draggedItemStart = useRef({ x: 0, y: 0 });

  // Mutable ref to latest state for event handlers
  const stateRef = useRef({
    projects, agents, zones, zoom, cameraX, cameraY,
    selectedProjectId, selectedZoneId, editMode,
  });
  stateRef.current = {
    projects, agents, zones, zoom, cameraX, cameraY,
    selectedProjectId, selectedZoneId, editMode,
  };

  // Track container size for centering
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Convert pointer event to world coordinates
  const getLocalCoords = useCallback((e: React.PointerEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { localX: 0, localY: 0 };
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const centerX = rect.width / 2 + stateRef.current.cameraX;
    const centerY = rect.height / 2.5 + stateRef.current.cameraY;
    return {
      localX: (screenX - centerX) / stateRef.current.zoom,
      localY: (screenY - centerY) / stateRef.current.zoom,
    };
  }, []);

  // ─── Pointer Handlers ─────────────────────────────
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const { localX, localY } = getLocalCoords(e);
    const { editMode, selectedProjectId, selectedZoneId } = stateRef.current;

    if (editMode && selectedProjectId) {
      const project = stateRef.current.projects.find((p) => p.id === selectedProjectId);
      if (project) {
        const [sx, sy] = gridToScreen(project.x, project.y);
        if (Math.sqrt((localX - sx) ** 2 + (localY - sy + 20) ** 2) < 40) {
          isDraggingProject.current = true;
          dragStart.current = { x: localX, y: localY };
          draggedItemStart.current = { x: project.x, y: project.y };
          containerRef.current?.setPointerCapture(e.pointerId);
          return;
        }
      }
    }

    if (editMode && selectedZoneId) {
      const zone = stateRef.current.zones.find((z) => z.id === selectedZoneId);
      if (zone) {
        const [brX, brY] = gridToScreen(zone.startX + zone.width, zone.startY + zone.height);
        if (Math.sqrt((localX - brX) ** 2 + (localY - brY) ** 2) < 15) {
          isResizingZone.current = true;
          dragStart.current = { x: localX, y: localY };
          draggedItemStart.current = { x: zone.width, y: zone.height };
          containerRef.current?.setPointerCapture(e.pointerId);
          return;
        }

        const [tlX, tlY] = gridToScreen(zone.startX, zone.startY);
        const cx = (tlX + brX) / 2;
        const cy = (tlY + brY) / 2;
        if (Math.sqrt((localX - cx) ** 2 + (localY - cy) ** 2) < 80) {
          isDraggingZone.current = true;
          dragStart.current = { x: localX, y: localY };
          draggedItemStart.current = { x: zone.startX, y: zone.startY };
          containerRef.current?.setPointerCapture(e.pointerId);
          return;
        }
      }
    }

    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    cameraStart.current = { x: stateRef.current.cameraX, y: stateRef.current.cameraY };
    setPanning(true);
    containerRef.current?.setPointerCapture(e.pointerId);
  }, [setPanning, getLocalCoords]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (isDraggingProject.current && stateRef.current.selectedProjectId) {
      const { localX, localY } = getLocalCoords(e);
      const dx = localX - dragStart.current.x;
      const dy = localY - dragStart.current.y;
      const gridDx = Math.round(dx / TILE_W + dy / TILE_H);
      const gridDy = Math.round(dy / TILE_H - dx / TILE_W);
      const newX = Math.max(0, Math.min(GRID_SIZE - 1, draggedItemStart.current.x + gridDx));
      const newY = Math.max(0, Math.min(GRID_SIZE - 1, draggedItemStart.current.y + gridDy));
      updateProject(stateRef.current.selectedProjectId, { x: newX, y: newY });
      return;
    }

    if (isDraggingZone.current && stateRef.current.selectedZoneId) {
      const { localX, localY } = getLocalCoords(e);
      const dx = localX - dragStart.current.x;
      const dy = localY - dragStart.current.y;
      const gridDx = Math.round(dx / TILE_W + dy / TILE_H);
      const gridDy = Math.round(dy / TILE_H - dx / TILE_W);
      updateZone(stateRef.current.selectedZoneId, {
        startX: Math.max(0, draggedItemStart.current.x + gridDx),
        startY: Math.max(0, draggedItemStart.current.y + gridDy),
      });
      return;
    }

    if (isResizingZone.current && stateRef.current.selectedZoneId) {
      const { localX, localY } = getLocalCoords(e);
      const dx = localX - dragStart.current.x;
      const dy = localY - dragStart.current.y;
      const gridDx = Math.round(dx / TILE_W + dy / TILE_H);
      const gridDy = Math.round(dy / TILE_H - dx / TILE_W);
      updateZone(stateRef.current.selectedZoneId, {
        width: Math.max(1, draggedItemStart.current.x + gridDx),
        height: Math.max(1, draggedItemStart.current.y + gridDy),
      });
      return;
    }

    if (isDragging.current) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setCamera(cameraStart.current.x + dx, cameraStart.current.y + dy);
    }
  }, [setCamera, getLocalCoords, updateProject, updateZone]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (isDraggingProject.current || isDraggingZone.current || isResizingZone.current) {
      if (isDraggingProject.current && stateRef.current.selectedProjectId) {
        const p = stateRef.current.projects.find((pr) => pr.id === stateRef.current.selectedProjectId);
        if (p) updateProject(p.id, { x: p.x, y: p.y }, { immediate: true });
      }
      if ((isDraggingZone.current || isResizingZone.current) && stateRef.current.selectedZoneId) {
        const z = stateRef.current.zones.find((zn) => zn.id === stateRef.current.selectedZoneId);
        if (z) updateZone(z.id, { startX: z.startX, startY: z.startY, width: z.width, height: z.height }, { immediate: true });
      }
      isDraggingProject.current = false;
      isDraggingZone.current = false;
      isResizingZone.current = false;
      return;
    }

    const dx = Math.abs(e.clientX - dragStart.current.x);
    const dy = Math.abs(e.clientY - dragStart.current.y);
    if (dx < 5 && dy < 5) {
      const { localX, localY } = getLocalCoords(e);
      const clickedAgent = findAgentAtPosition(localX, localY, stateRef.current.projects, stateRef.current.agents);
      if (clickedAgent) {
        selectAgent(clickedAgent.id);
      } else {
        const clickedProject = findProjectAtPosition(localX, localY, stateRef.current.projects);
        if (clickedProject) {
          selectProject(clickedProject.id);
        } else {
          const clickedZone = findZoneAtPosition(localX, localY, stateRef.current.zones);
          if (clickedZone) {
            selectZone(clickedZone.id);
          } else {
            selectProject(null);
          }
        }
      }
    }

    isDragging.current = false;
    setPanning(false);
  }, [selectProject, selectAgent, selectZone, setPanning, getLocalCoords, updateProject, updateZone]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const scaleFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.3, Math.min(3, zoom * scaleFactor));
    setZoom(newZoom);
  }, [zoom, setZoom]);

  const handlePointerLeave = useCallback(() => {
    isDragging.current = false;
    isDraggingProject.current = false;
    isDraggingZone.current = false;
    isResizingZone.current = false;
    setPanning(false);
  }, [setPanning]);

  // Pre-compute agents per project once (avoids O(n*m) re-filter per building per animation frame).
  // Derived from useUnifiedAgents (live terminal / chat / workflow activity) rather than the
  // worldStore.agents table, which is never populated. This is what makes a building show whether
  // agents are actually working on its project. Finished/dead rows are dropped so they don't orbit forever.
  const agentsByProjectId = useMemo(() => {
    const map = new Map<number, Agent[]>();
    for (const group of unifiedGroups) {
      if (group.projectId == null) continue; // Ungrouped agents have no building to orbit
      for (const row of group.rows) {
        const visuals = unifiedStatusToAgentVisuals(row.status);
        if (!visuals) continue; // completed / stale — nothing actively working
        const synthetic: Agent = {
          id: row.id,
          projectId: group.projectId,
          state: visuals.state,
          name: row.title,
          attentionState: visuals.attentionState,
        };
        const list = map.get(group.projectId);
        if (list) list.push(synthetic);
        else map.set(group.projectId, [synthetic]);
      }
    }
    return map;
  }, [unifiedGroups]);

  // Sort projects: coding first (upper-left), marketing second (lower-right), then z-order within each group
  const sortedProjects = useMemo(() => {
    const coding = projects.filter(p => p.project_category !== "marketing");
    const marketing = projects.filter(p => p.project_category === "marketing");
    const sortByDepth = (a: Project, b: Project) => a.x + a.y - (b.x + b.y);
    return [...coding.sort(sortByDepth), ...marketing.sort(sortByDepth)];
  }, [projects]);

  const { hasMarketingDistrict, hasCodingDistrict } = useMemo(() => ({
    hasMarketingDistrict: projects.some(p => p.project_category === "marketing"),
    hasCodingDistrict: projects.some(p => p.project_category !== "marketing"),
  }), [projects]);

  // SVG transform for pan/zoom
  const cx = containerSize.width / 2 + cameraX;
  const cy = containerSize.height / 2.5 + cameraY;

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative overflow-hidden select-none"
      style={{
        background: isDark ? "#0F0F11" : "#f8fafc",
        cursor: isDragging.current ? "grabbing" : "grab",
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onWheel={handleWheel}
      data-testid="container-pixi-world"
    >
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "none" }}>
        <defs>
          <pattern id="dotGrid" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="10" cy="10" r="0.5" fill="#222226" />
          </pattern>
        </defs>

        {/* World content — pan/zoom transform */}
        <g transform={`translate(${cx}, ${cy}) scale(${zoom})`}>
          {/* Dot grid — inside transform so it scales with the world */}
          {isDark && <rect x={-600} y={-300} width={1200} height={900} fill="url(#dotGrid)" opacity={0.6} />}

          {/* Grid */}
          <IsoGrid visible={showGrid} isDark={isDark} />

          {/* Floating particles */}
          {isDark && <Particles count={15} />}

          {/* Zones */}
          {zones.map((zone) => (
            <ZoneOverlay
              key={zone.id}
              zone={zone}
              isSelected={zone.id === selectedZoneId}
              editMode={editMode}
              showLabel={showZoneLabels}
            />
          ))}

          {/* Pipelines */}
          {pipelines.map((pipeline) => (
            <PipelineSVG key={pipeline.id} pipeline={pipeline} projects={projects} zones={zones} isDark={isDark} />
          ))}

          {/* District labels & separator */}
          {hasCodingDistrict && hasMarketingDistrict && (
            <>
              <DistrictDivider isDark={isDark} />
              <DistrictLabel gx={2} gy={1} label="Coding District" color={isDark ? "#4ade80" : "#16a34a"} />
              <DistrictLabel gx={9} gy={10} label="Marketing District" color={isDark ? "#c084fc" : "#7c3aed"} />
            </>
          )}

          {/* Buildings (sorted for z-order) */}
          {sortedProjects.map((project) => (
            <BuildingSVG
              key={project.id}
              project={project}
              agents={agentsByProjectId.get(project.id) ?? []}
              isSelected={project.id === selectedProjectId}
              editMode={editMode}
              pulse={pulse}
            />
          ))}

          {/* Cloud resource dots */}
          {projects.map((project) => {
            const resources = cloudResources[project.id];
            if (!resources || resources.length === 0) return null;
            return <CloudResourceDots key={`cloud-${project.id}`} project={project} resources={resources} />;
          })}
        </g>
      </svg>
    </div>
  );
}
